import React from "react";
import { Button } from "@/components/ui/button";
import {
  Loader2,
  Image as ImageIcon,
  FileText,
  Trash2,
  RefreshCw,
  Upload,
} from "lucide-react";

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "bmp",
  "ico",
  "avif",
];

function getExtension(value?: string | null): string {
  if (!value) return "";
  const match = /\.([a-z0-9]+)(?:[?#].*)?$/i.exec(value);
  return match ? match[1].toLowerCase() : "";
}

function isImageFile(url?: string | null, fileName?: string | null): boolean {
  const ext = getExtension(fileName) || getExtension(url);
  if (!ext) return true;
  return IMAGE_EXTENSIONS.includes(ext);
}

interface ThumbnailDisplayProps {
  thumbnailUrl?: string | null;
  thumbnailFileName?: string;
  loading?: boolean;
  onSelect: () => void;
  onRemove: () => void;
  acceptedFormatsHint?: string;
  /** Noun for the empty-state prompt, e.g. "thumbnail", "hero banner". */
  label?: string;
}

export default function ThumbnailDisplay({
  thumbnailUrl,
  thumbnailFileName,
  loading,
  onSelect,
  onRemove,
  acceptedFormatsHint,
  label = "thumbnail",
}: ThumbnailDisplayProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center p-6 border border-dashed border-gray-300 dark:border-slate-600 rounded-md">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500 dark:text-gray-400 mr-2" />
        <span className="text-gray-500 dark:text-gray-400">Loading...</span>
      </div>
    );
  }

  if (thumbnailUrl) {
    const isImage = isImageFile(thumbnailUrl, thumbnailFileName);
    const FileIcon = isImage ? ImageIcon : FileText;
    const extension =
      getExtension(thumbnailFileName) || getExtension(thumbnailUrl);

    return (
      <div className="relative group max-w-md">
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
          <div className="relative">
            {isImage ? (
              <>
                <img
                  alt={thumbnailFileName || "Thumbnail"}
                  className="w-full h-64 object-cover"
                  src={thumbnailUrl}
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-200" />
              </>
            ) : (
              <div className="w-full h-64 flex flex-col items-center justify-center gap-2 bg-gray-100 dark:bg-slate-800">
                <FileText className="h-16 w-16 text-gray-400 dark:text-gray-500" />
                {extension && (
                  <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {extension}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="p-4 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700">
            <div className="flex items-center justify-between gap-2">
              <a
                className="flex items-center gap-2 min-w-0 text-sm text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:underline"
                download={thumbnailFileName || true}
                href={thumbnailUrl}
                rel="noopener noreferrer"
                target="_blank"
                title={
                  thumbnailFileName
                    ? `Download ${thumbnailFileName}`
                    : "Download file"
                }
              >
                <FileIcon className="text-blue-500 flex-shrink-0" size={16} />
                <span className="truncate max-w-[200px]">
                  {thumbnailFileName || "File"}
                </span>
              </a>
              <div className="flex items-center gap-1">
                <Button
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={onSelect}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <RefreshCw className="mr-1" size={14} />
                  Replace
                </Button>
                <Button
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={onRemove}
                  size="sm"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 className="mr-1 text-red-500" size={14} />
                  Remove
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md">
      <button
        className="w-full border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-8 text-center hover:border-gray-400 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-800 transition-all cursor-pointer group"
        onClick={onSelect}
        type="button"
      >
        <Upload className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400 transition-colors mb-3" />
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Click to upload {label}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          or browse from File Manager
        </p>
        {acceptedFormatsHint && (
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            {acceptedFormatsHint}
          </p>
        )}
      </button>
    </div>
  );
}
